import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "../Reveal";

/**
 * Reusable terracotta CTA strip.
 * Defaults reflect Home page copy; pass props on other pages (e.g. /collection) for variants.
 *
 * Props:
 *  eyebrow, heading (string or node - supports <span class="italic"> inline),
 *  subtext, primary { label, href, external?, onClick? }, secondary { ... }
 */
const defaults = {
  eyebrow: "· The Aurevia Invitation ·",
  heading: (
    <>
      Ready to see marble{" "}
      <span className="italic">in a new light?</span>
    </>
  ),
  subtext:
    "Browse our collection or request a free sample visit. No obligation. No showroom.",
  primary: { label: "Browse Collection", href: "/collection" },
  secondary: { label: "Request Free Samples", href: "/request-samples" },
};

const isExternal = (href) =>
  typeof href === "string" && /^https?:\/\//.test(href);

const ActionButton = ({ action, primary, testId }) => {
  const navigate = useNavigate();
  const external = action.external ?? isExternal(action.href);

  const baseClasses = primary
    ? "group inline-flex items-center justify-center gap-3 bg-brand-cream px-8 py-4 text-brand-charcoal font-sans text-xs font-medium uppercase tracking-[0.24em] transition-all duration-300 hover:bg-brand-charcoal hover:text-brand-cream"
    : "group inline-flex items-center justify-center gap-3 border border-brand-charcoal/40 px-8 py-4 text-brand-charcoal font-sans text-xs font-medium uppercase tracking-[0.24em] transition-all duration-300 hover:border-brand-charcoal hover:bg-brand-charcoal hover:text-brand-cream";

  if (external) {
    return (
      <a
        href={action.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className={baseClasses}
      >
        {action.label}
        {primary && (
          <ArrowRight
            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
            strokeWidth={2}
          />
        )}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        if (action.onClick) action.onClick();
        else if (action.href) navigate(action.href);
      }}
      data-testid={testId}
      className={baseClasses}
    >
      {action.label}
      {primary && (
        <ArrowRight
          className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
          strokeWidth={2}
        />
      )}
    </button>
  );
};

export const FinalCTA = ({
  eyebrow = defaults.eyebrow,
  heading = defaults.heading,
  subtext = defaults.subtext,
  primary = defaults.primary,
  secondary = defaults.secondary,
  testIdPrimary = "final-cta-primary",
  testIdSecondary = "final-cta-secondary",
}) => {
  return (
    <section
      data-testid="section-final-cta"
      className="relative overflow-hidden"
      style={{ backgroundColor: "#C97B5A" }}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.18] mix-blend-multiply pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 10% 30%, rgba(26,26,26,0.4), transparent 55%), radial-gradient(circle at 85% 70%, rgba(26,26,26,0.35), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(26,26,26,0.10) 0%, transparent 35%, transparent 65%, rgba(26,26,26,0.12) 100%)",
        }}
      />

      <div className="editorial-container relative py-16 md:py-24">
        <div className="max-w-4xl">
          <Reveal>
            <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-charcoal/70 mb-6">
              {eyebrow}
            </p>
            <h2
              data-testid="final-cta-heading"
              className="font-serif font-light text-balance text-brand-charcoal leading-[1.02] tracking-tight"
              style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}
            >
              {heading}
            </h2>
            <p className="mt-7 max-w-2xl font-sans text-base md:text-lg leading-relaxed text-brand-charcoal/80">
              {subtext}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-4">
              <ActionButton action={primary} primary testId={testIdPrimary} />
              <ActionButton
                action={secondary}
                primary={false}
                testId={testIdSecondary}
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
